import { Container, Graphics, Text, TextStyle } from "pixi.js";
import type { KeySpan } from "@/lib/harmony/key-spans";

/**
 * The tonality margin of the roll.
 *
 * The chords and their degrees own the left edge (see `Harmony`); the key the
 * degrees are counted from owns the right one. Nothing is drawn while the
 * piece stays in its key. Where a section departs from it — the verse read on
 * the relative minor — a hairline runs down the right edge for as long as the
 * departure lasts, and one line of text marks the line where it begins: the
 * key abbreviated, and one word for what it is to the piece's key ("Ré m ·
 * relatif"). The return to the piece's key is marked too, in a neutral tone,
 * and gets no hairline: the hairline says "you are away from home".
 *
 * # No plate behind the text, and one line rather than two
 *
 * The first draft filled a rounded plate and stacked "Ré mineur" over
 * "relatif de Fa majeur". On this surface that is a hole: the roll is where
 * the notes pass, and an opaque tag hides the very thing the player came to
 * read. So the text is set over the notes with a drop shadow — the way the
 * chord names already are, ten pixels to the left of here — and it is cut to
 * one short line. The piece's key is written in the toolbar; this margin only
 * has to say which key the degrees are counted from, and that it is not the
 * usual one.
 *
 * The mark behaves like the chord labels: it rides with its line, and while
 * the playhead is inside the span it stays pinned at the playhead until the
 * next change pushes it down. Alpha fades with the distance in seconds, like
 * the chord lines, but keeps a high floor: a change of key is worth seeing
 * from afar.
 *
 * Achromatic except for the accent violet, which the octave grid already
 * reserves for the one structural landmark of the surface (middle C): the
 * two hands own every other hue here, and the chord lines their root colours.
 */

type KeySpanLabel = {
  /** The key, abbreviated: "Ré m". */
  key: string;
  /** One word for what it is to the piece's key: "relatif". */
  relation: string;
};

type KeySpansProps = {
  spans: readonly KeySpan[];
  labelOf: (span: KeySpan) => KeySpanLabel;
  currentTick: number;
  currentSeconds: number;
  tickToSeconds: (tick: number) => number;
  fromTick: number;
  toTick: number;
  width: number;
  height: number;
  playheadY: number;
};

type KeySpanSlot = {
  rail: Graphics;
  /** Height baked into the rail geometry; -1 if not yet drawn. */
  railHeight: number;
  tick: Graphics;
  tickWidth: number;
  keyText: Text;
  relationText: Text;
};

const ACCENT = 0xa78bfa;
const ACCENT_BRIGHT = 0xc4b5fd;
const NEUTRAL = 0xcbd5e1;

/** Clear of the minimap, which starts where the roll ends. */
const RIGHT_INSET = 14;
const RAIL_WIDTH = 2;
const RAIL_ALPHA = 0.26;
/** The tick that marks the line itself, under the text. */
const TICK_LENGTH = 16;
/** Between the key and the word that qualifies it. */
const LABEL_GAP = 5;
/** Between the text and the line it marks. */
const LABEL_ABOVE = 3;
/** Height of the text row, used to space a pushed mark. */
const LABEL_HEIGHT = 15;

/** Max opacity of a tag at the playhead, and its floor far away. */
const TAG_ALPHA_MAX = 1;
const TAG_ALPHA_MIN = 0.55;
/** Distance (in seconds) over which the tag fades to its floor. */
const FADE_RANGE_SECONDS = 4;

export class KeySpans {
  private container: Container;
  private previousProps?: KeySpansProps;

  private pool: KeySpanSlot[] = [];
  private poolIndex = 0;
  private highWaterMark = 0;
  private framesSinceLastShrink = 0;

  constructor(private stage: Container) {
    this.container = new Container();
    this.stage.addChild(this.container);
  }

  destroy(): void {
    for (const slot of this.pool) {
      this.destroySlot(slot);
    }

    this.pool.length = 0;
    this.poolIndex = 0;
    this.container.destroy({ children: true });
  }

  draw(props: KeySpansProps): void {
    if (this.shouldSkip(props)) {
      return;
    }

    const { spans, labelOf, currentTick, currentSeconds, tickToSeconds, fromTick, toTick, width, height, playheadY } =
      props;
    const pxPerTick = height / (toTick - fromTick);
    const yOf = (tick: number) => height - (tick - fromTick) * pxPerTick;

    this.resetPool();

    for (let i = 0; i < spans.length; i++) {
      const span = spans[i];
      const away = span.relation !== "home";

      // The piece opens in its own key: nothing to say there.
      if (i === 0 && !away) {
        continue;
      }

      if (span.toTick < fromTick || span.fromTick > toTick + (toTick - fromTick)) {
        continue;
      }

      const naturalY = yOf(span.fromTick);
      const endY = Math.max(yOf(span.toTick), 0);
      const next = spans[i + 1];
      const isActive = currentTick >= span.fromTick && currentTick < span.toTick;

      const slot = this.acquire();
      const label = labelOf(span);
      this.setText(slot.keyText, label.key);
      this.setText(slot.relationText, `· ${label.relation}`);

      const tone = away ? ACCENT : NEUTRAL;
      slot.keyText.style.fill = away ? ACCENT_BRIGHT : NEUTRAL;

      // ── The hairline down the right edge, for the length of the departure ──
      if (away) {
        const top = Math.max(endY, 0);
        const bottom = Math.min(naturalY, height);
        const railHeight = Math.max(bottom - top, 0);

        if (railHeight > 0) {
          if (slot.railHeight !== railHeight) {
            slot.rail.clear();
            slot.rail.rect(0, 0, RAIL_WIDTH, railHeight);
            slot.rail.fill({ color: 0xffffff, alpha: 1 });
            slot.railHeight = railHeight;
          }

          slot.rail.tint = ACCENT;
          slot.rail.alpha = RAIL_ALPHA;
          slot.rail.x = width - RIGHT_INSET - RAIL_WIDTH;
          slot.rail.y = top;
          slot.rail.visible = true;
        } else {
          slot.rail.visible = false;
        }
      } else {
        slot.rail.visible = false;
      }

      // ── The mark at the line where the key changes ──
      // Pinned at the playhead while the span is being played and its start
      // has gone under the playhead; the next change pushes it down.
      let y = naturalY;

      if (isActive && naturalY > playheadY) {
        y = playheadY;

        if (next) {
          const gap = playheadY - yOf(next.fromTick);

          if (gap < LABEL_HEIGHT) {
            y = playheadY + (LABEL_HEIGHT - gap);
          }
        }
      }

      if (naturalY < -1 || naturalY > height + 1) {
        // The line itself is out of frame: only a pinned mark survives.
        if (!(isActive && naturalY > playheadY)) {
          slot.tick.visible = false;
          slot.keyText.visible = false;
          slot.relationText.visible = false;

          continue;
        }
      }

      const distSeconds = isActive ? 0 : Math.abs(tickToSeconds(span.fromTick) - currentSeconds);
      const t = Math.min(distSeconds / FADE_RANGE_SECONDS, 1);
      const alpha = TAG_ALPHA_MAX + (TAG_ALPHA_MIN - TAG_ALPHA_MAX) * t;

      if (slot.tickWidth !== TICK_LENGTH) {
        slot.tick.clear();
        slot.tick.moveTo(0, 0);
        slot.tick.lineTo(TICK_LENGTH, 0);
        slot.tick.stroke({ color: 0xffffff, alpha: 1, width: 1 });
        slot.tickWidth = TICK_LENGTH;
      }

      slot.tick.tint = tone;
      slot.tick.alpha = alpha * 0.55;
      slot.tick.x = width - RIGHT_INSET - TICK_LENGTH;
      slot.tick.y = naturalY;
      slot.tick.visible = naturalY >= 0 && naturalY <= height;

      // One line, right-aligned against the edge, read left to right:
      // the key, then the word that qualifies it.
      const right = width - RIGHT_INSET;
      const textY = y - LABEL_ABOVE - slot.keyText.height;

      slot.relationText.x = right - slot.relationText.width;
      slot.relationText.y = textY + (slot.keyText.height - slot.relationText.height);
      slot.relationText.alpha = alpha * 0.75;
      slot.relationText.visible = true;

      slot.keyText.x = slot.relationText.x - LABEL_GAP - slot.keyText.width;
      slot.keyText.y = textY;
      slot.keyText.alpha = alpha;
      slot.keyText.visible = true;
    }

    this.hideUnused();

    if (this.poolIndex > this.highWaterMark) {
      this.highWaterMark = this.poolIndex;
    }

    this.framesSinceLastShrink++;

    if (this.framesSinceLastShrink >= 300) {
      this.shrinkPool();
      this.framesSinceLastShrink = 0;
    }
  }

  private setText(text: Text, value: string): void {
    if (text.text !== value) {
      text.text = value;
    }
  }

  // ── Pool management ───────────────────────────────────────────

  private acquire(): KeySpanSlot {
    if (this.poolIndex < this.pool.length) {
      return this.pool[this.poolIndex++];
    }

    const rail = new Graphics();
    const tick = new Graphics();

    // A drop shadow rather than a plate: the notes must stay visible under
    // the text. Same reasoning, same numbers, as the chord names.
    const shadow = { color: 0x000000, blur: 6, distance: 0, alpha: 0.85 };

    const keyText = new Text({
      text: "",
      style: new TextStyle({
        fontSize: 11,
        fontWeight: "600",
        fontFamily: "Inter, sans-serif",
        letterSpacing: 0.6,
        fill: ACCENT_BRIGHT,
        dropShadow: shadow,
      }),
    });

    const relationText = new Text({
      text: "",
      style: new TextStyle({
        fontSize: 10,
        fontWeight: "400",
        fontFamily: "Inter, sans-serif",
        letterSpacing: 0.4,
        fill: 0xffffff,
        dropShadow: shadow,
      }),
    });
    relationText.style.fill = { color: 0xffffff, alpha: 0.62 };

    for (const node of [rail, tick, keyText, relationText]) {
      node.visible = false;
      this.container.addChild(node);
    }

    const slot: KeySpanSlot = {
      rail,
      railHeight: -1,
      tick,
      tickWidth: -1,
      keyText,
      relationText,
    };
    this.pool.push(slot);
    this.poolIndex++;

    return slot;
  }

  private destroySlot(slot: KeySpanSlot): void {
    slot.rail.destroy();
    slot.tick.destroy();
    slot.keyText.destroy();
    slot.relationText.destroy();
  }

  private resetPool(): void {
    this.poolIndex = 0;
  }

  private hideUnused(): void {
    for (let i = this.poolIndex; i < this.pool.length; i++) {
      const slot = this.pool[i];
      slot.rail.visible = false;
      slot.tick.visible = false;
      slot.keyText.visible = false;
      slot.relationText.visible = false;
    }
  }

  private shrinkPool(): void {
    const targetSize = Math.ceil(this.highWaterMark * 1.2);

    if (this.pool.length > targetSize) {
      for (let i = targetSize; i < this.pool.length; i++) {
        this.destroySlot(this.pool[i]);
      }

      this.pool.length = targetSize;
    }

    this.highWaterMark = 0;
  }

  private shouldSkip(props: KeySpansProps): boolean {
    const prev = this.previousProps;
    this.previousProps = props;

    if (!prev) {
      return false;
    }

    return (
      prev.fromTick === props.fromTick &&
      prev.toTick === props.toTick &&
      prev.currentTick === props.currentTick &&
      prev.width === props.width &&
      prev.height === props.height &&
      prev.playheadY === props.playheadY &&
      prev.spans === props.spans &&
      prev.labelOf === props.labelOf
    );
  }
}
